import { type FC, useContext } from 'react';
import classnames from 'classnames';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import ChairIcon from 'dibs-icons/exports/legacy/ChairOutlined.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { ACTIONS, STEPS } from './GetHelpZendeskConstants.ts';

import { type GetHelpZendeskSellerItemsListItem_item$key } from './__generated__/GetHelpZendeskSellerItemsListItem_item.graphql.ts';

import styles from './GetHelpZendeskSellerItemsListItem.scss';
import dibsCss from 'dibs-css';

type Props = {
    item: GetHelpZendeskSellerItemsListItem_item$key | null | undefined;
};

const GetHelpZendeskSellerItemsListItem: FC<Props> = ({ item: itemRef }) => {
    const { dispatch } = useContext(GetHelpZendeskContext);

    const item = useFragment(
        graphql`
            fragment GetHelpZendeskSellerItemsListItem_item on Item {
                serviceId
                title
                firstPhotoWebPath(size: small)
                dealerReference
                dibsReference
                dealerStatusDisplay
            }
        `,
        itemRef
    );
    const {
        serviceId,
        title,
        firstPhotoWebPath,
        dealerReference,
        dibsReference,
        dealerStatusDisplay,
    } = item || {};

    return (
        <Link
            dataTn="get-help-modal-orders-list-order"
            onClick={() => {
                dispatch({ type: ACTIONS.SET_ITEM_ID, itemId: serviceId || '' });
                dispatch({ type: ACTIONS.SET_ORDER_ID, orderId: '' });
                dispatch({ type: ACTIONS.SET_STEP, step: STEPS.SELLER_ITEM });
            }}
            underline="none"
            className={dibsCss.wFull}
        >
            <span
                className={classnames(
                    styles.link,
                    dibsCss.flex,
                    dibsCss.border,
                    dibsCss.borderSolid,
                    dibsCss.borderDolphin,
                    dibsCss.roundedXxsmall,
                    dibsCss.pSmall,
                    dibsCss.prMedium,
                    dibsCss.gapSmall,
                    dibsCss.textNoir
                )}
            >
                <span
                    className={classnames(dibsCss.hXxlarge, dibsCss.wXxlarge, dibsCss.minWXxlarge)}
                >
                    {firstPhotoWebPath ? (
                        <img
                            src={firstPhotoWebPath}
                            alt={title || ''}
                            className={classnames(
                                dibsCss.hFull,
                                dibsCss.wFull,
                                dibsCss.objectCover
                            )}
                        />
                    ) : (
                        <ChairIcon />
                    )}
                </span>
                <span
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.justifyCenter,
                        dibsCss.gapXsmall,
                        dibsCss.overflowHidden,
                        dibsCss.textLeft
                    )}
                >
                    <span
                        className={classnames(
                            dibsCss.block,
                            dibsCss.sassyFontBodyMediumHeavy,
                            dibsCss.truncate
                        )}
                    >
                        {title}
                    </span>
                    <span>
                        {dealerReference ? (
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskSellerItemsListItem.sellerRef"
                                defaultMessage="Seller Ref {sellerRef}"
                                values={{ sellerRef: dealerReference }}
                            />
                        ) : (
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskSellerItemsListItem.itemRef"
                                defaultMessage="Item Ref {itemRef}"
                                values={{ itemRef: dibsReference }}
                            />
                        )}
                        {dealerStatusDisplay && (
                            <>
                                {' | '}
                                <span className={dibsCss.sassyFontBodySmallHeavy}>
                                    {dealerStatusDisplay}
                                </span>
                            </>
                        )}
                    </span>
                </span>
            </span>
        </Link>
    );
};

export { GetHelpZendeskSellerItemsListItem };
